import React, { useEffect, useState } from 'react';
import { adminApi } from '../lib/adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  BarChart, Bar,
} from 'recharts';
import { Upload, Trash2, Pencil, Plus, RefreshCw } from 'lucide-react';

// Module-level constants to avoid re-creating inline objects on every render
const CHART_BOX_280 = { width: '100%', height: 280 };
const CHART_BOX_260 = { width: '100%', height: 260 };
const CHART_MARGIN_LINE = { top: 5, right: 20, left: 0, bottom: 5 };
const CHART_MARGIN_BAR = { left: 60 };
const AXIS_TICK_11 = { fontSize: 11 };

/* ---------------- Analytics ---------------- */
export const AnalyticsPanel = () => {
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [topCats, setTopCats] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [days, setDays] = useState(30);
  const { toast } = useToast();

  const load = async () => {
    try {
      const [s, t, c, v] = await Promise.all([
        adminApi.analyticsSummary(),
        adminApi.analyticsTimeline(days),
        adminApi.analyticsTopCategories(10),
        adminApi.analyticsTopVendors(10),
      ]);
      setSummary(s); setTimeline(t.timeline || []); setTopCats(c || []); setTopVendors(v || []);
    } catch (err) {
      toast({ title: 'Failed to load analytics', description: err?.message, variant: 'destructive' });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (!summary) return <div className="p-10 text-gray-500">Loading analytics…</div>;

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Analytics</h1>
          <p className="text-sm text-gray-600">Marketplace KPIs and trends.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="text-sm border border-gray-300 rounded px-2 py-1" data-testid="analytics-days-select">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button variant="outline" size="sm" onClick={load} data-testid="analytics-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Active Vendors', val: summary.vendors.active, sub: `${summary.vendors.pending} pending`, color: 'from-emerald-500 to-emerald-700' },
          { label: 'Products', val: summary.products, sub: '', color: 'from-orange-500 to-[#ee5e23]' },
          { label: 'Customers', val: summary.customers, sub: `${summary.new_customers_7d} new (7d)`, color: 'from-cyan-500 to-cyan-700' },
          { label: 'Enquiries (7d)', val: summary.enquiries.last_7_days, sub: `${summary.enquiries.total} total`, color: 'from-rose-500 to-rose-700' },
        ].map((k) => (
          <div key={k.label} className={`bg-gradient-to-br ${k.color} text-white rounded-lg p-5 shadow`} data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]/g,'-')}`}>
            <div className="text-3xl font-bold">{k.val}</div>
            <div className="text-sm opacity-90 mt-1">{k.label}</div>
            {k.sub && <div className="text-xs opacity-75 mt-1">{k.sub}</div>}
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold mb-3">Activity Trend ({days} days)</h3>
        <div style={CHART_BOX_280}>
          <ResponsiveContainer>
            <LineChart data={timeline} margin={CHART_MARGIN_LINE}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={AXIS_TICK_11} />
              <YAxis tick={AXIS_TICK_11} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="enquiries" stroke="#ee5e23" strokeWidth={2} />
              <Line type="monotone" dataKey="requirements" stroke="#2c54b2" strokeWidth={2} />
              <Line type="monotone" dataKey="customers" stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold mb-3">Top Sub-categories by Products</h3>
          <div style={CHART_BOX_260}>
            <ResponsiveContainer>
              <BarChart data={topCats} layout="vertical" margin={CHART_MARGIN_BAR}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" allowDecimals={false} tick={AXIS_TICK_11} />
                <YAxis dataKey="name" type="category" width={120} tick={AXIS_TICK_11} />
                <Tooltip />
                <Bar dataKey="product_count" fill="#2c54b2" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold mb-3">Top Vendors by Leads</h3>
          {topVendors.length === 0 ? <div className="text-sm text-gray-500">No leads yet.</div> : (
            <div style={CHART_BOX_260}>
              <ResponsiveContainer>
                <BarChart data={topVendors} layout="vertical" margin={CHART_MARGIN_BAR}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis type="number" allowDecimals={false} tick={AXIS_TICK_11} />
                  <YAxis dataKey="name" type="category" width={120} tick={AXIS_TICK_11} />
                  <Tooltip />
                  <Bar dataKey="lead_count" fill="#ee5e23" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------------- CSV Import ---------------- */
const ImportCard = ({ kind, file, setFile, label, busy, onSubmit, onDownloadTemplate }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5">
    <div className="flex items-center justify-between mb-2">
      <h3 className="font-semibold">{label}</h3>
      <button onClick={() => onDownloadTemplate(kind)} className="text-xs text-[#2c54b2] hover:underline" data-testid={`template-${kind}`}>Download template</button>
    </div>
    <input
      type="file"
      accept=".csv"
      onChange={(e) => setFile(e.target.files?.[0] || null)}
      className="text-sm block w-full mb-3"
      data-testid={`import-file-${kind}`}
    />
    <div className="flex gap-2">
      <Button variant="outline" disabled={busy || !file} onClick={() => onSubmit(kind, file, true)} data-testid={`dry-run-${kind}`}>
        Dry run
      </Button>
      <Button disabled={busy || !file} onClick={() => onSubmit(kind, file, false)} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid={`import-${kind}`}>
        <Upload className="w-4 h-4 mr-1" /> Import
      </Button>
    </div>
  </div>
);

export const ImportPanel = () => {
  const { toast } = useToast();
  const [vendorFile, setVendorFile] = useState(null);
  const [productFile, setProductFile] = useState(null);
  const [cityFile, setCityFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (kind, file, dryRun) => {
    if (!file) return toast({ title: 'Pick a CSV file', variant: 'destructive' });
    setBusy(true);
    setResult(null);
    try {
      const fnMap = {
        vendors: adminApi.importVendorsCsv,
        products: adminApi.importProductsCsv,
        cities: adminApi.importCitiesCsv,
      };
      const fn = fnMap[kind];
      const r = await fn(file, dryRun);
      setResult({ kind, dryRun, ...r });
      toast({ title: dryRun ? 'Dry run complete' : `${kind} imported`, description: `${r.created} created, ${r.skipped_duplicates} skipped, ${r.errors.length} errors` });
    } catch (err) {
      toast({ title: 'Import failed', description: err?.response?.data?.detail || err?.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const downloadTemplate = (kind) => {
    const map = {
      vendors: 'name,contact_name,city,mobile,email,website,gst_number,year_established,business_type,categories,description,status\nAcme Steel,Rahul Sharma,Hyderabad,9000011111,info@acme.com,https://acmesteel.com,29ABCDE1234F1Z2,2015,Manufacturer,Steel|Metal,Premium steel supplier,active\n',
      products: 'name,price,unit,moq,sub_category_id,supplier_id,image,gst,description,verified,slug\nMS Round Steel Pipe 40mm,75,Kilogram,100,sub-building-construction-tmt-bars,s1,https://example.com/pipe.jpg,GST Verified,Round steel pipe 40mm,true,ms-round-steel-pipe-40mm\n',
      cities: 'name,order,is_active\nNizamabad,10,true\nKarimnagar,11,true\nMahbubnagar,12,true\n',
    };
    const blob = new Blob([map[kind]], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${kind}_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="import-panel">
      <div>
        <h1 className="text-2xl font-bold">Bulk Import</h1>
        <p className="text-sm text-gray-600">Upload CSV files to onboard vendors, products and cities in bulk. Use dry-run first to validate.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <ImportCard kind="vendors" file={vendorFile} setFile={setVendorFile} label="Import Vendors" busy={busy} onSubmit={submit} onDownloadTemplate={downloadTemplate} />
        <ImportCard kind="products" file={productFile} setFile={setProductFile} label="Import Products" busy={busy} onSubmit={submit} onDownloadTemplate={downloadTemplate} />
        <ImportCard kind="cities" file={cityFile} setFile={setCityFile} label="Import Cities" busy={busy} onSubmit={submit} onDownloadTemplate={downloadTemplate} />
      </div>
      {result && (
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="import-result">
          <div className="font-semibold mb-2">{result.dryRun ? 'Dry run' : 'Import'} result — {result.kind}</div>
          <div className="text-sm text-gray-700">Created: <b>{result.created}</b> • Skipped duplicates: <b>{result.skipped_duplicates}</b> • Errors: <b>{result.errors.length}</b></div>
          {result.errors.length > 0 && (
            <div className="mt-2 text-xs text-rose-600">
              {result.errors.map((e, i) => <div key={`err-${e.row}-${i}`}>Row {e.row}: {e.error}</div>)}
            </div>
          )}
          {result.sample.length > 0 && (
            <div className="mt-2 text-xs text-gray-600">
              <div className="font-medium">Sample (dry-run only):</div>
              <pre className="whitespace-pre-wrap">{JSON.stringify(result.sample, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------------- Lead Rules ---------------- */
export const LeadRulesPanel = () => {
  const { toast } = useToast();
  const [rules, setRules] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [r, v] = await Promise.all([adminApi.listLeadRules(), adminApi.list('suppliers')]);
      setRules(r || []); setVendors(v || []);
    } catch (err) {
      toast({ title: 'Failed to load rules', description: err?.message, variant: 'destructive' });
      setRules([]);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (payload) => {
    try {
      const cleaned = { ...payload, priority: Number(payload.priority || 100), active: !!payload.active };
      if (editing?.id) await adminApi.updateLeadRule(editing.id, cleaned);
      else await adminApi.createLeadRule(cleaned);
      toast({ title: editing?.id ? 'Rule updated' : 'Rule created' });
      setEditing(null);
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message, variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try { await adminApi.removeLeadRule(id); toast({ title: 'Deleted' }); load(); }
    catch (err) { toast({ title: 'Delete failed', description: err?.message, variant: 'destructive' }); }
  };

  return (
    <div className="space-y-4" data-testid="lead-rules-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Lead Routing Rules</h1>
          <p className="text-sm text-gray-600">When an enquiry has no product, route it to a vendor based on city / keyword.</p>
        </div>
        <Button onClick={() => setEditing({ name: '', city: '', keyword: '', assigned_vendor_id: vendors[0]?.id || '', priority: 100, active: true })} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="add-rule-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Rule
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {rules === null ? <div className="p-10 text-center text-gray-500">Loading…</div>
        : rules.length === 0 ? <div className="p-10 text-center text-gray-500">No rules yet. Add one to start routing enquiries.</div>
        : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Name','City','Keyword','Vendor','Priority','Active','Hits','Actions'].map((h) => <th key={h} className="px-4 py-2 text-left font-semibold text-gray-700">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{r.name}</td>
                  <td className="px-4 py-2">{r.city || '—'}</td>
                  <td className="px-4 py-2">{r.keyword || '—'}</td>
                  <td className="px-4 py-2">{r.vendor_name || r.assigned_vendor_id}</td>
                  <td className="px-4 py-2">{r.priority}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 text-xs rounded ${r.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{r.active ? 'Yes' : 'No'}</span>
                  </td>
                  <td className="px-4 py-2">{r.hit_count || 0}</td>
                  <td className="px-4 py-2">
                    <button onClick={() => setEditing(r)} className="p-1.5 text-gray-600 hover:text-[#2c54b2] hover:bg-blue-50 rounded transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(r.id)} className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()} data-testid="rule-form">
            <h3 className="font-semibold mb-3">{editing.id ? 'Edit Rule' : 'New Rule'}</h3>
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="rule-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>City (prefix match)</Label><Input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} placeholder="e.g. Hyderabad" data-testid="rule-city" /></div>
                <div><Label>Keyword (case-insensitive)</Label><Input value={editing.keyword} onChange={(e) => setEditing({ ...editing, keyword: e.target.value })} placeholder="e.g. steel" data-testid="rule-keyword" /></div>
              </div>
              <div><Label>Assigned Vendor *</Label>
                <select value={editing.assigned_vendor_id} onChange={(e) => setEditing({ ...editing, assigned_vendor_id: e.target.value })} className="w-full text-sm border border-gray-300 rounded px-2 py-2" data-testid="rule-vendor-select">
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.city})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Priority (lower = matched first)</Label><Input type="number" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} data-testid="rule-priority" /></div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="rule-active" />
                    Active
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={() => save(editing)} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="save-rule-btn">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- Audit Logs ---------------- */
export const AuditLogsPanel = () => {
  const { toast } = useToast();
  const [logs, setLogs] = useState(null);

  const load = async () => {
    try { setLogs(await adminApi.listAuditLogs(200)); }
    catch (err) { toast({ title: 'Failed to load audit logs', description: err?.message, variant: 'destructive' }); setLogs([]); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="audit-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Audit Log</h1>
          <p className="text-sm text-gray-600">Sensitive admin actions, in reverse chronological order.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="audit-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {logs === null ? <div className="p-10 text-center text-gray-500">Loading…</div>
        : logs.length === 0 ? <div className="p-10 text-center text-gray-500">No audit entries yet.</div>
        : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['When','Action','Target','Details'].map((h) => <th key={h} className="px-4 py-2 text-left font-semibold text-gray-700">{h}</th>)}</tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap text-gray-600">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2"><span className="px-2 py-0.5 text-xs rounded bg-blue-50 text-[#2c54b2] font-medium">{l.action}</span></td>
                  <td className="px-4 py-2">{l.target_type}{l.target_id ? ` / ${l.target_id}` : ''}</td>
                  <td className="px-4 py-2 text-xs text-gray-600 max-w-md truncate">{l.meta && Object.keys(l.meta).length ? JSON.stringify(l.meta) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
